import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import type Geometry from '@arcgis/core/geometry/Geometry.js';
import type Point from '@arcgis/core/geometry/Point.js';
import {
  registerHandle,
  resolve,
  summarizeRef,
  type ResolvedRef,
  type Source,
  SourceSchema,
} from '@/agent/refs';
import { shortestPathOverNetwork } from './shared/astar';
import { linearToEsri, type LinearUnit } from './shared/units';
import type { Tool } from './types';

type Input = {
  from: Source;
  to: Source;
  network_layer_id?: string;
  units?: LinearUnit;
  max_snap_distance?: number;
};

function representativePoint(geom: Geometry): Point | null {
  if (geom.type === 'point') return geom as Point;
  const centroid = (geom as { centroid?: Point | null }).centroid;
  if (centroid) return centroid;
  const ext = (geom as { extent?: __esri.Extent | null }).extent;
  return ext?.center ?? null;
}

export const computeShortestPath: Tool<Input> = {
  name: 'compute_shortest_path',
  description:
    'A* shortest path between two Sources over a road network (default network_layer_id="roads"). Builds a graph from the network polylines around the endpoints, runs A*, and returns a polyline handle, the route distance, and the snapped endpoints. Endpoints farther than `max_snap_distance` metres (default 200) from any network feature are rejected — raise this when an endpoint lies off-road. Pair with render(highlight) + view_zoom_to to visualise the route on the map.',
  input_schema: {
    type: 'object',
    properties: {
      from: SourceSchema,
      to: SourceSchema,
      network_layer_id: { type: 'string' },
      units: { enum: ['meters', 'kilometers', 'feet', 'miles'] },
      max_snap_distance: {
        type: 'number',
        description:
          'Maximum distance (metres) an endpoint may be from the nearest network feature before the call fails. Default 200.',
        minimum: 0,
      },
    },
    required: ['from', 'to'],
    additionalProperties: false,
  },
  execute: async ({
    from,
    to,
    network_layer_id = 'roads',
    units = 'meters',
    max_snap_distance,
  }) => {
    const [a, b] = await Promise.all([resolve(from), resolve(to)]);
    if (!a.geometry || !b.geometry) {
      throw new Error('compute_shortest_path: both sources need geometry');
    }
    const aPt = representativePoint(a.geometry);
    const bPt = representativePoint(b.geometry);
    if (!aPt || !bPt) {
      throw new Error('compute_shortest_path: could not derive representative points from inputs');
    }

    const result = await shortestPathOverNetwork({
      fromPoint: aPt,
      toPoint: bPt,
      networkLayerId: network_layer_id,
      maxSnapDistance: max_snap_distance,
    });

    const distance = geometryEngine.geodesicLength(result.polyline, linearToEsri(units));

    const ref: ResolvedRef = {
      source_kind: 'geometry',
      geometry: result.polyline,
      extent: result.polyline.extent ?? undefined,
    };
    const handle = registerHandle(ref);

    const sr = result.polyline.spatialReference;
    return {
      handle,
      distance,
      units,
      network_layer_id,
      segment_count: result.polyline.paths[0]?.length ?? 0,
      nodes_explored: result.nodes_explored,
      from_point: { x: result.fromSnap.x, y: result.fromSnap.y, wkid: sr?.wkid ?? undefined },
      to_point: { x: result.toSnap.x, y: result.toSnap.y, wkid: sr?.wkid ?? undefined },
      summary: summarizeRef(ref),
    };
  },
};
